// src/features/locker-types/components/LockerTypeActions.tsx

import React, { useState } from 'react';
import {
  useTranslate,
  useRedirect,
} from 'react-admin';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Box,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useLockerTypePermissions } from '../hooks/useLockerTypePermissions';
import { LockerType } from '../types';
import BaseDeleteDialog from '../../../components/common/BaseDeleteDialog';

interface LockerTypeActionsProps {
  record: LockerType;
  small?: boolean;
}

const LockerTypeActions: React.FC<LockerTypeActionsProps> = ({ record, small = false }) => {
  const translate = useTranslate();
  const redirect = useRedirect();
  const permissions = useLockerTypePermissions(record);
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    redirect(`/locker-types/${record.id}`);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setShowDeleteDialog(true);
  };

  const handleDeleteClose = () => {
    setShowDeleteDialog(false);
  };

  const handleDeleteConfirm = () => {
    // BaseDeleteDialog maneja la eliminación internamente
    setShowDeleteDialog(false);
  };

  // Versión compacta para listas (si se necesita)
  if (small) {
    return (
      <Box sx={{ display: 'flex', gap: 1 }}>
        {permissions.canEditThis && (
          <Tooltip title={translate('ra.action.edit')}>
            <IconButton size="small" onClick={handleEdit}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {permissions.canDeleteThis && (
          <Tooltip title={translate('ra.action.delete')}>
            <IconButton size="small" onClick={handleDeleteClick}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        
        {showDeleteDialog && (
          <BaseDeleteDialog
            open={showDeleteDialog}
            onClose={handleDeleteClose}
            onConfirm={handleDeleteConfirm}
            resource="locker-types"
            recordId={record.id}
            record={record}
            title={translate('resources.locker-types.dialogs.delete_title', 'Eliminar Tipo de Taquilla')}
            message={translate(
              'resources.locker-types.dialogs.delete_message',
              { name: record.name }
            )}
            warningMessage={translate(
              'resources.locker-types.dialogs.delete_warning',
              'Esta acción no se puede deshacer.'
            )}
          />
        )}
      </Box>
    );
  }

  // Versión completa con menú
  return (
    <>
       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title={translate('ra.action.menu', 'Más opciones')}>
                <IconButton
                  size={'small'}
                  onClick={handleMenuOpen}
                  sx={{ color: 'action.active' }}
                >
                  <MoreVertIcon fontSize={'small'} />
                </IconButton>
              </Tooltip>
            </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {permissions.canEditThis && (
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {translate('ra.action.edit', 'Editar')}
            </ListItemText>
          </MenuItem>
        )}

        {/* Separador si hay más acciones */}
        {(permissions.canEditThis || permissions.canDeleteThis) && (
          <MenuItem divider />
        )}

        {permissions.canDeleteThis && (
          <MenuItem onClick={handleDeleteClick}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>
              {translate('ra.action.delete', 'Eliminar')}
            </ListItemText>
          </MenuItem>
        )}
      </Menu>

      {showDeleteDialog && (
        <BaseDeleteDialog
          open={showDeleteDialog}
          onClose={handleDeleteClose}
          onConfirm={handleDeleteConfirm}
          resource="locker-types"
          recordId={record.id}
          record={record}
          title={translate('resources.locker-types.dialogs.delete_title', 'Eliminar Tipo de Taquilla')}
          message={translate(
            'resources.locker-types.dialogs.delete_message',
            { name: record.name }
          )}
          warningMessage={translate(
            'resources.locker-types.dialogs.delete_warning',
            'Esta acción no se puede deshacer.'
          )}
        />
      )}
    </>
  );
};

export default LockerTypeActions;